import { useEffect, useMemo, useState } from 'react'
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Modal, Toaster } from '../design/index.js'
import type { SessionView } from '@project/contracts'
import { ApiProvider, apiFor } from './api.js'
import { ErrorBoundary } from './ErrorBoundary.js'
import { LoginPage } from './LoginPage.js'
import { Shell } from './Shell.js'
import { navigate, paths, useRoute, usePendingNavigation, type SignedInRoute } from './router.js'
import { NoticeBlock } from '../framework/feedback.js'
import { ListPage } from '../framework/ListPage.js'
import { DocumentPage } from '../framework/DocumentPage.js'
import { Home } from '../pages/Home.js'
import { TransactionsHub } from '../pages/TransactionsHub.js'
import { MasterListPage, MasterRecordPage, MastersHub } from '../framework/MasterPages.js'
import { ReportPage, ReportsHub } from '../framework/ReportPages.js'
import { DashboardPage, DashboardsHub } from '../framework/DashboardPages.js'
import { AlertsPage, ApprovalsPage, FavouritesPage, HelpPage, TasksPage } from '../pages/InboxPages.js'
import { SettingsPage } from '../pages/SettingsPage.js'
import { DrivePage } from '../pages/DrivePage.js'
import { AdminHub, AdminPage, ChangePasswordPage } from '../pages/AdminPages.js'
import { WorkListsPage } from '../pages/WorkListsPage.js'
import { QuotePortal } from '../pages/QuotePortal.js'
import { PlatformApp } from './PlatformApp.js'
import '../design/tokens.css'
import './sheet.css'

const UnbuiltSection = ({ title }: { title: string }) => (
  <div className="page page--narrow">
    <h1 className="page-title">{title}</h1>
    <div style={{ marginTop: 28 }}>
      <Empty
        title={`${title} is not part of this build.`}
        body="The document framework — lists, forms, approvals and the exception engine — is what ships here."
      />
    </div>
  </div>
)

const NotFound = ({ path }: { path: string }) => (
  <div className="page page--narrow">
    <h1 className="page-title">Page not found</h1>
    <p className="page-subtitle">Nothing is routed at “{path}”.</p>
  </div>
)

const HUB_TITLES: Record<string, string> = {
  favourites: 'Favourites',
  masters: 'Masters',
  reports: 'Reports',
  dashboards: 'Dashboards',
  approvals: 'Approvals',
  tasks: 'Tasks',
  alerts: 'Alerts',
  help: 'Help',
  worklists: 'Work lists',
}

const Page = ({ route }: { route: SignedInRoute }) => {
  switch (route.name) {
    case 'home':
      return <Home companyId={route.companyId} />
    case 'hub':
      if (route.hub === 'transactions') return <TransactionsHub companyId={route.companyId} />
      if (route.hub === 'masters') return <MastersHub companyId={route.companyId} />
      if (route.hub === 'reports') return <ReportsHub companyId={route.companyId} />
      if (route.hub === 'dashboards') return <DashboardsHub companyId={route.companyId} />
      if (route.hub === 'approvals') return <ApprovalsPage companyId={route.companyId} />
      if (route.hub === 'tasks') return <TasksPage companyId={route.companyId} />
      if (route.hub === 'alerts') return <AlertsPage companyId={route.companyId} />
      if (route.hub === 'favourites') return <FavouritesPage companyId={route.companyId} />
      if (route.hub === 'help') return <HelpPage companyId={route.companyId} />
      if (route.hub === 'worklists') return <WorkListsPage companyId={route.companyId} />
      return <UnbuiltSection title={HUB_TITLES[route.hub] ?? route.hub} />
    case 'settings':
      return <SettingsPage companyId={route.companyId} />
    case 'admin':
      return route.section
        ? <AdminPage companyId={route.companyId} section={route.section} />
        : <AdminHub companyId={route.companyId} />
    case 'password':
      return <ChangePasswordPage companyId={route.companyId} />
    // Keyed by which list, report or dashboard it is: moving from one to the
    // next is a new page, not the old one's filters applied to another query.
    case 'list':
      return <ListPage key={route.docRoute} companyId={route.companyId} docRoute={route.docRoute} creating={false} query={route.query} />
    case 'create':
      return <ListPage key={route.docRoute} companyId={route.companyId} docRoute={route.docRoute} creating query={route.query} />
    case 'document':
      return <DocumentPage companyId={route.companyId} docRoute={route.docRoute} id={route.id} />
    case 'masterList':
      return <MasterListPage key={route.masterRoute} companyId={route.companyId} masterRoute={route.masterRoute} query={route.query} />
    case 'masterRecord':
      return <MasterRecordPage companyId={route.companyId} masterRoute={route.masterRoute} id={route.id} />
    case 'report':
      return <ReportPage key={route.reportRoute} companyId={route.companyId} reportRoute={route.reportRoute} query={route.query} />
    case 'dashboard':
      return <DashboardPage key={route.dashboardRoute} companyId={route.companyId} dashboardRoute={route.dashboardRoute} query={route.query} />
    // The Project Drive tile on Reports routes here, and nothing rendered it.
    case 'drive':
      return <DrivePage companyId={route.companyId} query={route.query} />
    case 'unknown':
      return <NotFound path={route.path} />
  }
}

const UnsavedChangesGuard = () => {
  const { target, discard, stay } = usePendingNavigation()
  if (!target) return null
  return (
    <Modal
      title="Unsaved changes"
      tone="warn"
      onClose={stay}
      actions={
        <>
          <Button kind="ghost" onClick={discard}>Yes, don&rsquo;t save changes</Button>
          <Button kind="primary" onClick={stay}>No, stay in this page</Button>
        </>
      }
    >
      <p>You have some unsaved changes in this page. Do you want to leave this page without saving?</p>
    </Modal>
  )
}

/**
 * Everything below the session gate.
 *
 * The company in the URL is the one the user is looking at; on first load
 * there is none, so the session's own company is used. The old build defaulted
 * to the literal '1', which the API rejected with 400 on every request.
 */
const SignedIn = ({ session, route }: { session: SessionView; route: SignedInRoute }) => {
  // A company in the URL is only honoured if it is one of yours. A stale link —
  // a bookmark from before a reseed, or someone else's copied URL — falls back
  // to your own company rather than dead-ending on a refusal from the server.
  // Someone still on the password an administrator handed them is asked to
  // choose their own before anything else. It is a nudge, not a lock: the
  // server does not treat a temporary password as less authorised.
  const nudgePassword = session.mustChangePassword && route.name !== 'password'
  const known = session.companies.some((c) => c.id === route.companyId)
  const companyId = known ? route.companyId : session.companyId
  const api = useMemo(() => apiFor(companyId), [companyId])

  useEffect(() => {
    if (!known) navigate(paths.home(session.companyId), { force: true })
  }, [known, session.companyId])

  return (
    <ApiProvider value={api}>
      <Shell route={route} session={session}>
        {nudgePassword ? (
          <div className="page-banner">
            <NoticeBlock tone="warn">
              You are still using the temporary password you were given.{' '}
              <a href={paths.password(companyId)}>Choose your own</a>.
            </NoticeBlock>
          </div>
        ) : null}
        <ErrorBoundary key={`${route.name}:${'id' in route ? route.id : ''}`}>
          <Page route={route} />
        </ErrorBoundary>
      </Shell>
      <UnsavedChangesGuard />
    </ApiProvider>
  )
}

const Gate = () => {
  const route = useRoute()
  const api = useMemo(() => apiFor(''), [])
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.session(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const client = useQueryClient()

  // The supplier portal is answered before the session is even asked for: a
  // supplier has no account here, and must not be shown a sign-in page.
  if (route.name === 'quote') return <QuotePortal token={route.token} />

  // The platform console has its own identity, its own cookie and its own
  // client, so it is answered before the tenant session is asked for too — a
  // platform admin holds no tenant session and must not be shown the ordinary
  // sign-in page.
  if (route.name === 'platform') return <PlatformApp section={route.section} />

  if (session.isPending) {
    return <div style={{ minHeight: '100vh', background: 'var(--canvas)' }} />
  }
  if (session.isError || !session.data) {
    return (
      <ApiProvider value={api}>
        <LoginPage onSignedIn={() => { void client.invalidateQueries() }} />
      </ApiProvider>
    )
  }
  return <SignedIn session={session.data} route={route} />
}

export const App = () => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
        },
      }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <Gate />
      {/* Outside the routes, so a toast raised just before a navigation survives it. */}
      <Toaster />
    </QueryClientProvider>
  )
}
